import { useEffect, useState } from "react";
import api, { getAllRecords } from "../api";

const empty = {
  vehicleNumber: "",
  vehicleType: "Mini Truck",
  fuelType: "Diesel",
  loadCapacity: "",
  loadCapacityUnit: "KG",
  vendorId: "",
  client: "",
  clientLocation: "",
  supplierPrice: "",
  supplierPaymentTerm: "",
};
const paymentTerms = [
  ["daily", "One day payment"],
  ["weekly", "Weekly"],
  ["fifteen_days", "15 days"],
  ["monthly", "Monthly"],
];

export default function StaffVehiclePage() {
  const [rows, setRows] = useState([]),
    [vendors, setVendors] = useState([]),
    [clients, setClients] = useState([]),
    [locations, setLocations] = useState([]),
    [form, setForm] = useState(empty),
    [edit, setEdit] = useState(null),
    [showForm, setShowForm] = useState(false),
    [message, setMessage] = useState(""),
    [query, setQuery] = useState(""),
    [page, setPage] = useState(1),
    [loading, setLoading] = useState(true);
  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const pagedRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const selectedVendor = vendors.find((item) => item._id === form.vendorId);
  const isOwnVehicle = /uride/i.test(selectedVendor?.name || "");
  const load = async () => {
    setLoading(true);
    try {
      const [vehicleRows, vendorResult, clientResult] = await Promise.all([
        getAllRecords("/vehicles", { query }),
        api.get("/vehicles/vendors"),
        api.get("/clients"),
      ]);
      setRows(vehicleRows);
      setVendors(vendorResult.data.data);
      setClients(
        (clientResult.data.data || []).filter(
          (item) => item.status === "active",
        ),
      );
    } catch (e) {
      setMessage(
        e.response?.data?.message ||
          "Unable to load vehicle registration data.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setPage(1);
    load();
  }, [query]);
  useEffect(() => {
    if (!form.client) {
      setLocations([]);
      return;
    }
    api
      .get(`/locations/client/${form.client}`, { params: { active: true } })
      .then((response) => setLocations(response.data.data || []))
      .catch(() => setLocations([]));
  }, [form.client]);
  const set = (key) => (event) =>
    setForm({
      ...form,
      [key]: event.target.value,
      ...(key === "client" ? { clientLocation: "" } : {}),
      ...(key === "vendorId"
        ? { supplierPrice: "", supplierPaymentTerm: "" }
        : {}),
    });
  const closeForm = () => {
    setShowForm(false);
    setEdit(null);
    setForm(empty);
    setLocations([]);
  };
  const submit = async (event) => {
    event.preventDefault();
    try {
      await (edit
        ? api.put(`/vehicles/${edit._id}`, form)
        : api.post("/vehicles", form));
      setMessage(edit ? "Vehicle updated." : "Vehicle added.");
      closeForm();
      load();
    } catch (error) {
      setMessage(error.response?.data?.message || "Unable to save vehicle.");
    }
  };
  const modify = (row) => {
    setEdit(row);
    setForm({
      ...empty,
      ...row,
      vendorId: row.vendorId?._id || row.vendorId,
      client: row.client?._id || row.client,
      clientLocation: row.clientLocation?._id || row.clientLocation,
    });
    setShowForm(true);
  };
  return (
    <div className="vehicle-workspace">
      <div className="vehicle-heading">
        <div>
          <p>FLEET MANAGEMENT</p>
          <h2>Vehicle Management</h2>
        </div>
        <div className="vehicle-actions">
          <input
            placeholder="Search vehicle number"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            className="add-vehicle-button"
            onClick={() => {
              setEdit(null);
              setForm(empty);
              setShowForm(true);
            }}
          >
            + Add Vehicle
          </button>
        </div>
      </div>
      {message && <div className="vehicle-toast">{message}</div>}
      <div className="vehicle-layout">
        <section>
          <h3>Vehicle List</h3>
          {loading ? (
            <p>Loading vehicles…</p>
          ) : (
            <div className="vehicle-table action-table">
              <table>
                <thead>
                  <tr>
                    <th>Vehicle Number</th>
                    <th>Type</th>
                    <th>Vehicle Supplier</th>
                    <th>Client / Delivery Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => (
                    <tr key={row._id}>
                      <td>{row.vehicleNumber}</td>
                      <td>{row.vehicleType}</td>
                      <td>{row.vendorName}</td>
                      <td>
                        {row.clientName || "-"}
                        {row.clientLocationName
                          ? ` / ${row.clientLocationName}`
                          : ""}
                      </td>
                      <td>
                        <button onClick={() => modify(row)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                  {!rows.length && (
                    <tr>
                      <td colSpan="5">No vehicles found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!loading && rows.length > pageSize && <nav className="table-pagination" aria-label="Vehicle list pages"><button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>{Array.from({ length: pageCount }, (_, index) => <button key={index + 1} className={page === index + 1 ? "active" : ""} onClick={() => setPage(index + 1)}>{index + 1}</button>)}<button disabled={page === pageCount} onClick={() => setPage(page + 1)}>Next</button></nav>}
        </section>
      </div>
      {showForm && (
        <div
          className="vehicle-modal-backdrop"
          role="presentation"
        >
          <form
            className="vehicle-modal"
            onSubmit={submit}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p>{edit ? "EDIT VEHICLE" : "NEW VEHICLE"}</p>
                <h3>{edit ? "Edit vehicle" : "Add vehicle"}</h3>
              </div>
              <button type="button" aria-label="Close form" onClick={closeForm}>
                ×
              </button>
            </header>
            <div className="vehicle-modal-fields">
              <label>
                Vehicle Number
                <input
                  value={form.vehicleNumber}
                  onChange={set("vehicleNumber")}
                  required
                  autoFocus
                />
              </label>
              <label>
                Vehicle Type
                <select value={form.vehicleType} onChange={set("vehicleType")}>
                  {[
                    "Mini Truck",
                    "Pickup",
                    "Tempo",
                    "Container",
                    "Trailer",
                    "Other",
                  ].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                Fuel Type
                <select value={form.fuelType} onChange={set("fuelType")}>
                  {["Diesel", "Petrol", "CNG", "Electric"].map((item) => (
                    <option key={item}>{item}</option>
                  ))}
                </select>
              </label>
              <label>
                Load Capacity
                <div className="capacity">
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.loadCapacity}
                    onChange={set("loadCapacity")}
                    required
                  />
                  <select
                    value={form.loadCapacityUnit}
                    onChange={set("loadCapacityUnit")}
                  >
                    <option>KG</option>
                    <option>Tons</option>
                  </select>
                </div>
              </label>
              <label className="wide">
                Vehicle Supplier (Vendor)
                <select
                  value={form.vendorId}
                  onChange={set("vendorId")}
                  required
                >
                  <option value="">Select vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor._id} value={vendor._id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Client
                <select value={form.client} onChange={set("client")} required>
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client._id} value={client._id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Client Delivery Location
                <select
                  value={form.clientLocation}
                  onChange={set("clientLocation")}
                  required
                  disabled={!form.client}
                >
                  <option value="">
                    {form.client
                      ? "Select delivery location"
                      : "Select client first"}
                  </option>
                  {locations.map((location) => (
                    <option key={location._id} value={location._id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <footer>
              <button type="button" className="cancel" onClick={closeForm}>
                Cancel
              </button>
              <button>{edit ? "Update Vehicle" : "Add Vehicle"}</button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
